import { MdDehaze, MdEuro, MdGridView, MdPallet, MdTimeline, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { TbPigMoney, TbMoneybag } from 'react-icons/tb';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@chakra-ui/hooks';

import Card from 'components/card';
import CreateButton from 'components/buttons/CreateButton';
import { getOrderDashboard, getPaginatedOrders, exportDeliveryNotes, exportPreparationSheets} from 'interfaces/order';
import { generatePreparation } from 'interfaces/orderPreparation';
import { getDeliveryTours } from 'interfaces/deliveryTour';
import LoadingCard from './components/loadingCard';
import MiniStatistics from 'components/card/MiniStatistics';
import OrderCard from 'components/card/orderCard';
import orderImg from 'assets/img/order.png';
import SearchIcon from 'components/icons/SearchIcon';
import Pagination from 'components/pagination/Pagination';
import ProductSummary from './components/productsSummary';
import ExportMenu from './components/exportMenu';
import { fetchFilter, setFilter } from 'utils/filtersStorage';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { dateToFrenchShort } from 'utils/date';
import DateRangePicker from 'components/dataPicker';
import QuickOrder from './components/QuickOrder';
import DevisTable from './components/DevisTable';
import { MdStore } from 'react-icons/md';
import { getInvoiceLineTotalHT, getTotalTTC, isCustomerHT } from 'views/orders/createOrders/utils';

const ListOrders = () => {
    const nav = useNavigate();
    const [orders, setOrders] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [firstFetch, setFirstFetch] = useState(true);
    const [limitPerPage, setLimitPerpage] = useState(8);
    const [ordersMeta, setOrdersMeta] = useState(null);
    const [orderType, setOrderType] = useState('all');
    const [view, setView] = useState('grid');
    const [sortBy, setSortBy] = useState('deliveryDate');
    const [toggleState, setToggleState] = useState('pending');
    const today = new Date();
    const twoMonthsAgo = new Date();
        twoMonthsAgo.setMonth(today.getMonth() - 2);
        twoMonthsAgo.setDate(1); // set to the start of the month for consistency
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const [range, setRange] = useState({
    startDate: twoMonthsAgo,
    endDate: endOfMonth,
});
    const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingPrep, setIsDownloadingPrep] = useState(false);
    const [isGeneratingPrep, setIsGeneratingPrep] = useState(false);
    const [deliveryTours, setDeliveryTours] = useState<any[]>([]);
    const [selectedTour, setSelectedTour] = useState<string>('all');
    const [tableSortColumn, setTableSortColumn] = useState<string>('orderId');
    const [tableSortDirection, setTableSortDirection] = useState<'asc' | 'desc'>('desc');
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const isParent = localStorage.getItem('ownerId');
    const { isOpen: isQuickOrderOpen, onOpen: onQuickOrderOpen, onClose: onQuickOrderClose } = useDisclosure();

    let display = false;
    if (!isParent) {
        display = true;
    } else if (permissions.includes('all') || permissions.includes('show:number-dashboard')) {
        display = true;
    }

    const canSeeDashboard = display;

    const changeToogleState = (state: string) => {
        setToggleState(state);
        setSelectedOrders([]);
        fetchOrders(1, limitPerPage, state, sortBy, selectedTour);
    };

    const handleDateChange = (dates: any) => {
        const startDate = new Date(dates.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(dates.endDate);
        endDate.setHours(23, 59, 59, 999);
        setRange((prevFilter: any) => ({
            ...prevFilter,
            startDate,
            endDate,
        }));
    };

    const fetchOrders = async (
        page: number,
        limit: number,
        state: string = toggleState,
        sortBy: string,
        tourId: string = selectedTour,
        rangeParam: any = range,
        searchParam: string = search,
    ) => {
        if (state === 'devis') {
            setOrders([]);
            setOrdersMeta(null);
            return;
        }
        setLimitPerpage(limit);
        setOrders(null);
        const res = await getPaginatedOrders(
            page,
            limit,
            state,
            searchParam,
            orderType,
            sortBy,
            rangeParam,
            tourId,
        );
        if (res) {
            setOrders(res.data);
            setOrdersMeta(res.meta);
            setPage(page);
            if (firstFetch) setFirstFetch(false);
        }
    };

    const initOrdersDashboard = async () => {
        if (!canSeeDashboard) {
                 setDashboard(null);
                 return;
               }
               const res = await getOrderDashboard();
               if (res) setDashboard(res);
    };

    const initDeliveryTours = async () => {
        const res = await getDeliveryTours(false);
        if (res) setDeliveryTours(res.data || []);
    };

    useEffect(() => {
        // The initial load is entirely handled by the mount effect below,
        // which resolves cached filters (including range) synchronously
        // and fetches with them directly. This avoids a duplicate fetch
        // on mount using stale (pre-cache) state.
        if (firstFetch) return;
        fetchOrders(1, limitPerPage, toggleState, sortBy, selectedTour);
    }, [orderType, sortBy, range, selectedTour]);

    useEffect(() => {
        setOrderType('all');
    }, [toggleState]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            nav('/login');
        }
        const filter = fetchFilter('orders');

        const resolvedToggleState = filter?.toggleState || toggleState;
        const resolvedSearch = filter?.search || '';
        const resolvedSelectedTour = filter?.selectedTour || selectedTour;
        const resolvedSortBy = filter?.sortBy || sortBy;
        const resolvedView = filter?.view || view;
        const resolvedPage = filter?.page || page;
        const resolvedLimit = filter?.limitPerPage || limitPerPage;

        let resolvedRange = range;
        if (filter?.range) {
            resolvedRange = {
                startDate: new Date(filter.range.startDate),
                endDate: new Date(filter.range.endDate),
            };
        }

        setToggleState(resolvedToggleState);
        setSearch(resolvedSearch);
        setSelectedTour(resolvedSelectedTour);
        setSortBy(resolvedSortBy);
        setView(resolvedView);
        setRange(resolvedRange);

        // Fetch directly with the resolved values instead of relying on
        // state set above: those setState calls only take effect on the
        // next render, so any effect closing over `range`/`search` right
        // now would still see the pre-cache defaults.
        fetchOrders(
            resolvedPage,
            resolvedLimit,
            resolvedToggleState,
            resolvedSortBy,
            resolvedSelectedTour,
            resolvedRange,
            resolvedSearch,
        );

        initOrdersDashboard();
        initDeliveryTours();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!firstFetch)
            setFilter('orders', {
                page,
                limitPerPage,
                search,
                toggleState,
                view,
                sortBy,
                range,
                selectedTour,
            });
    }, [
        page,
        limitPerPage,
        search,
        toggleState,
        firstFetch,
        view,
        sortBy,
        range,
        selectedTour,
    ]);

    useEffect(() => {
        // The initial fetch (with or without cache) is already triggered
        // once, directly, by the mount effect above.
        if (firstFetch) return;

        setOrders(null);
        setOrdersMeta(null);
        const delayDebounceFn = setTimeout(() => {
            fetchOrders(1, limitPerPage, toggleState, sortBy, selectedTour);
        }, 600);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars

    function timeAgo(date: string) {
        const now: any = new Date();
        const givenDate: any = new Date(date);
        const diffInSeconds = Math.floor((now - givenDate) / 1000);

        const days = Math.floor(diffInSeconds / (3600 * 24));
        const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        if (days >= 5) {
            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(givenDate);
        }

        if (days > 0) {
            return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
        } else if (hours > 0) {
            return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
        } else if (minutes > 0) {
            return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
        } else {
            return `Il y a ${seconds} seconde${seconds > 1 ? 's' : ''}`;
        }
    }

    function isWithin30Minutes(date: Date | string): boolean {
        const start = new Date();
        const end = new Date(date);
        const diffInMs = Math.abs(end.getTime() - start.getTime());
        const diffInMinutes = diffInMs / (1000 * 60);
        return diffInMinutes <= 30;
    }

    const toggleOrderSelection = (id: string) => {
        if (selectedOrders.includes(id)) {
            setSelectedOrders(selectedOrders.filter((orderId) => orderId !== id));
        } else {
            setSelectedOrders([...selectedOrders, id]);
        }
    };

    const toggleAllOrders = () => {
        if (!orders) return;
        const currentOrders = orders.filter((c: any) => c.status === toggleState);
        if (selectedOrders.length === currentOrders.length) {
            setSelectedOrders([]);
        } else {
            setSelectedOrders(currentOrders.map((o: any) => o._id));
        }
    };

    const handleExportDeliveryNotes = async () => {
        if (selectedOrders.length === 0) return;
        setIsDownloading(true);
        try {
            const blob = await exportDeliveryNotes(selectedOrders);
            if (blob) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `bons-de-livraison-${new Date().toISOString().slice(0, 10)}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleExportPreparationSheets = async () => {
        if (selectedOrders.length === 0) return;
        setIsDownloadingPrep(true);
        try {
            const blob = await exportPreparationSheets(selectedOrders);
            if (blob) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `feuilles-de-preparation-${new Date().toISOString().slice(0, 10)}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsDownloadingPrep(false);
        }
    };

    const handleGeneratePreparations = async () => {
        if (selectedOrders.length === 0) return;
        setIsGeneratingPrep(true);
        try {
            const results = await Promise.all(
                selectedOrders.map((orderId) => generatePreparation(orderId))
            );
            const succeeded = results.filter(Boolean).length;
            const failed = results.length - succeeded;
            if (failed > 0) {
                console.warn(`${failed} préparation(s) n'ont pas pu être générée(s)`);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsGeneratingPrep(false);
        }
    };

    const handleTableSort = (column: string) => {
        if (tableSortColumn === column) {
            setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setTableSortColumn(column);
            setTableSortDirection('asc');
        }
    };

    const getFilteredOrders = (ordersToFilter: any[]) => {
        if (toggleState === 'pending') {
            return ordersToFilter.filter((c: any) => c.status === 'pending' || c.status === 'draft');
        }
        return ordersToFilter.filter((c: any) => c.status === toggleState);
    };

    const toNumber = (value: any) => {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        const parsed = parseFloat(String(value).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const getOrderIsCustomerHT = (order: any) => {
        return (
            isCustomerHT(order?.customer) ||
            isCustomerHT(order) ||
            order?.customer?.isHT === true
        );
    };

    const getDisplayedTotalTTC = (order: any) => {
        if (Array.isArray(order?.items) && order.items.length > 0) {
            return getTotalTTC(order.items, {
                isCustomerHT: getOrderIsCustomerHT(order),
                globalDiscountType: order?.globalDiscountType,
                globalDiscountValue: order?.globalDiscountValue,
            });
        }
        return toNumber(order?.totalTTC);
    };

    const getOrderMarginSummary = (order: any) => {
        const items = Array.isArray(order?.items) ? order.items : [];
        let totalMargin = 0;
        let includedSellHT = 0;
        let excludedCount = 0;

        items.forEach((item: any) => {
            const purchasePrice = toNumber(
                item?.totalPurchasePrice ?? item?.purchasePrice,
            );
            const quantity = toNumber(item?.quantity);
            const hasPurchasePrice =
                (item?.totalPurchasePrice !== undefined &&
                    item?.totalPurchasePrice !== null &&
                    item?.totalPurchasePrice !== '') ||
                (item?.purchasePrice !== undefined &&
                    item?.purchasePrice !== null &&
                    item?.purchasePrice !== '');

            if (!hasPurchasePrice || quantity <= 0) {
                excludedCount += 1;
                return;
            }

            const lineHT = getInvoiceLineTotalHT([item]);
            includedSellHT += lineHT;
            totalMargin += lineHT - purchasePrice * quantity;
        });

        const marginRate =
            includedSellHT > 0 ? (totalMargin / includedSellHT) * 100 : null;

        return {
            totalMargin,
            marginRate,
            excludedCount,
            hasComputedMargin: includedSellHT > 0,
        };
    };

    return (
        <>
            <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
                {toggleState !== 'devis' && (
                <>
                <div className="z-0 col-span-12 grid h-full w-full grid-cols-2 gap-3 sm:gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <MiniStatistics
                        name="Commandes"
                        value={
                                         canSeeDashboard && orders
                                           ? orders.filter((c: any) => c.status === 'pending').length
                                           : 'null'
                                       }
                        icon={<MdPallet color="#0260CB" />}
                        iconBg="bg-lightPrimary"
                        extra="sm:hidden"
                    />
                    <MiniStatistics
                        name="Commandes en cours"
                        value={
                                         canSeeDashboard && orders
                                           ? orders.filter((c: any) => c.status === 'pending').length
                                           : '-'
                                       }
                        icon={<MdPallet color="#0260CB" />}
                        iconBg="bg-lightPrimary"
                        extra="hidden sm:flex"
                    />
                    <MiniStatistics
                        name="CA du jour"
                        value={
                                         canSeeDashboard && dashboard
                                           ? `${Math.round(dashboard.revenueOfTheDay)} €`
                                           : '-'
                                       }
                        subValue={
                            canSeeDashboard && dashboard && Math.round(dashboard.draftRevenueOfTheDay) > 0
                                ? `+${Math.round(dashboard.draftRevenueOfTheDay)} € en brouillon`
                                : undefined
                        }
                        icon={<MdEuro color="#0260CB" />}
                        iconBg="bg-lightPrimary"
                    />
                    <MiniStatistics
                        name="CA des 7 derniers jours"
                        value={
                                         canSeeDashboard && dashboard
                                           ? `${Math.round(dashboard.revenueoftheWeek)} €`
                                           : '-'
                                       }
                        subValue={
                            canSeeDashboard && dashboard && Math.round(dashboard.draftRevenueoftheWeek) > 0
                                ? `+${Math.round(dashboard.draftRevenueoftheWeek)} € en brouillon`
                                : undefined
                        }
                        icon={<TbMoneybag color="#0260CB" />}
                        iconBg="bg-lightPrimary"
                    />
                    <MiniStatistics
                        name="CA des 30 derniers jours"
                        value={
                                         canSeeDashboard && dashboard
                                           ? `${Math.round(dashboard.revenueOfTheMonth)} €`
                                           : '-'
                                       }
                        subValue={
                            canSeeDashboard && dashboard && Math.round(dashboard.draftRevenueOfTheMonth) > 0
                                ? `+${Math.round(dashboard.draftRevenueOfTheMonth)} € en brouillon`
                                : undefined
                        }
                        icon={<TbPigMoney color="#0260CB" />}
                        iconBg="bg-lightPrimary"
                    />
                </div>
                <div className="col-span-12">
                    <ProductSummary />
                </div>
                </>
                )}
                {/* <div className="col-span-12">
                    <DiscoverCommandPage />
                </div> */}
                <div className="col-span-12 h-full w-full rounded-[20px]">
                    <Card extra="pt-[10px] pl-[20px] pr-[20px] pb-[20px] border overflow-visible">
                        {
                            <>
                                {/* Status */}
                                <div className="w-full flex items-center m-auto overflow-x-auto overflow-y-visible">
                                    <div className="mt-2 flex w-full min-w-max gap-[2px] sm:gap-1 border-b border-gray-200 justify-start sm:justify-center flex-row px-2 sm:px-0">
                                        <div
                                            className={
                                                toggleState === 'pending'
                                                    ? 'border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                                    : 'pb-[9px] pt-[6px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                            }
                                            onClick={() => {
                                                changeToogleState('pending');
                                            }}
                                        >
                                            <p className="text-[10px] sm:text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                                                <span className="hidden md:inline">
                                                    En cours
                                                </span>
                                                <span className="inline md:hidden">
                                                    Cours
                                                </span>{' '}
                                                <span className="text-[9px] sm:text-[11px]">
                                                ({ordersMeta ? ordersMeta.pending : '-'})
                                                </span>
                                            </p>
                                        </div>

                                        <div
                                            className={
                                                toggleState === 'delivered'
                                                    ? 'border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                                    : 'pb-[9px] pt-[6px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                            }
                                            onClick={() => {
                                                changeToogleState('delivered');
                                            }}
                                        >
                                            <p className="text-[10px] sm:text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                                                <span className="hidden md:inline">
                                                    Livrées
                                                </span>
                                                <span className="inline md:hidden">
                                                    Livr.
                                                </span>{' '}
                                                <span className="text-[9px] sm:text-[11px]">
                                                ({ordersMeta ? ordersMeta.delivered : '-'})
                                                </span>
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                toggleState === 'canceled'
                                                    ? 'border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                                    : 'pb-[9px] pt-[6px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                            }
                                            onClick={() => {
                                                changeToogleState('canceled');
                                            }}
                                        >
                                            <p className="text-[10px] sm:text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                                                <span className="hidden md:inline">
                                                    Annulées
                                                </span>
                                                <span className="inline md:hidden">
                                                    Annul.
                                                </span>{' '}
                                                <span className="text-[9px] sm:text-[11px]">
                                                ({ordersMeta ? ordersMeta.canceled : '-'})
                                                </span>
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                toggleState === 'already-invoiced'
                                                    ? 'border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                                    : 'pb-[9px] pt-[6px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                            }
                                            onClick={() => {
                                                changeToogleState('already-invoiced');
                                            }}
                                        >
                                            <p className="text-[10px] sm:text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                                                <span className="hidden md:inline">
                                                    Facturées
                                                </span>
                                                <span className="inline md:hidden">
                                                    Fact.
                                                </span>{' '}
                                                <span className="text-[9px] sm:text-[11px]">
                                                ({ordersMeta ? ordersMeta.alreadyInvoiced : '-'})
                                                </span>
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                toggleState === 'devis'
                                                    ? 'border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] hover:cursor-default dark:border-brand-400 bg-[#7090B0]/[0.05] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                                    : 'pb-[9px] pt-[6px] hover:cursor-pointer bg-[#7090B0]/[0.15] flex items-center justify-center gap-1 rounded-t-[10px] sm:rounded-t-[15px] min-w-[90px] sm:w-[180px] lg:w-[210px] h-[36px] sm:h-[40px] text-center px-2 sm:px-3'
                                            }
                                            onClick={() => {
                                                changeToogleState('devis');
                                            }}
                                        >
                                            <p className="text-[10px] sm:text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                                                Devis
                                            </p>
                                        </div>
                                    </div>
                                    <div className="hidden sm:flex gap-1 mr-2 flex-shrink-0 relative">
                                        <div
                                            className={`flex items-center text-xl hover:cursor-pointer bg-none text-navy-700 bg-lightPrimary py-2 px-2 hover:bg-gray-100 linear justify-center rounded-lg transition duration-200`}
                                            onClick={() =>
                                                nav('/recurring-orders')
                                            }
                                        >
                                            <FaArrowRotateRight
                                                className="flex my-auto hover:cursor-pointer"
                                                size={15}
                                                title="Commandes récurrentes"
                                            />
                                        </div>
                                        <div
                                            className={`flex items-center text-xl hover:cursor-pointer bg-none text-navy-700 bg-lightPrimary py-2 px-2 hover:bg-gray-100 linear justify-center rounded-lg transition duration-200`}
                                            onClick={() =>
                                                nav('/orders/forecast')
                                            }
                                        >
                                            <MdTimeline
                                                className="flex my-auto hover:cursor-pointer"
                                                size={20}
                                                title="Prévisions des ventes"
                                            />
                                        </div>
                                        <ExportMenu range={range} />
                                    </div>
                                </div>
                                {/* Search */}
                                <div className="mt-[20px] flex flex-col gap-3">
                                    {/* Mobile buttons at top */}
                                    <div className="flex sm:hidden gap-2 justify-end relative">
                                        <div
                                            className="flex items-center text-base hover:cursor-pointer bg-none text-navy-700 bg-lightPrimary py-2 px-2 hover:bg-gray-100 linear justify-center rounded-lg transition duration-200"
                                            onClick={() => nav('/recurring-orders')}
                                        >
                                            <FaArrowRotateRight size={14} />
                                        </div>
                                        <div
                                            className="flex items-center text-base hover:cursor-pointer bg-none text-navy-700 bg-lightPrimary py-2 px-2 hover:bg-gray-100 linear justify-center rounded-lg transition duration-200"
                                            onClick={() => nav('/orders/forecast')}
                                        >
                                            <MdTimeline size={16} />
                                        </div>
                                        <ExportMenu range={range} />
                                    </div>
                                    
                                    {/* Search and filters */}
                                    <div className="w-full flex flex-col sm:flex-row gap-2 sm:gap-3">
                                        <div className="flex h-[38px] w-full items-center rounded-xl bg-lightPrimary text-sm text-gray-600 sm:flex-1">
                                            <SearchIcon />
                                            <input
                                                type="text"
                                                placeholder="Rechercher..."
                                                value={search}
                                                onChange={(e) =>
                                                    setSearch(e.target.value)
                                                }
                                                className="block w-full rounded-full bg-lightPrimary text-sm sm:text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="flex-1 sm:flex-none">
                                                <DateRangePicker
                                                    label=""
                                                    defaultStartDate={range.startDate}
                                                    defaultEndDate={range.endDate}
                                                    onChange={handleDateChange}
                                                />
                                            </div>
                                            <div className="flex-1 sm:flex-none">
                                                <select
                                                    value={selectedTour}
                                                    onChange={(e) => setSelectedTour(e.target.value)}
                                                    className="h-[38px] w-full rounded-xl bg-lightPrimary px-2 sm:px-3 text-xs sm:text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                                                >
                                                    <option value="all">Toutes</option>
                                                    {deliveryTours.map((tour: any) => (
                                                        <option key={tour._id} value={tour._id}>
                                                            {tour.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center sm:justify-end">
                                        {selectedOrders.length > 0 && (
                                            <>
                                                <button
                                                    className={`flex items-center justify-center text-xs sm:text-sm ${
                                                        !isGeneratingPrep
                                                            ? 'hover:bg-green-600'
                                                            : 'cursor-wait opacity-70'
                                                    } text-white bg-green-500 py-2 px-3 rounded-xl transition duration-200 h-10 w-full sm:w-auto`}
                                                    onClick={!isGeneratingPrep ? handleGeneratePreparations : undefined}
                                                    disabled={isGeneratingPrep}
                                                >
                                                    <span className="whitespace-nowrap">
                                                        {isGeneratingPrep ? 'Génération...' : (
                                                            <>
                                                                <span className="hidden sm:inline">Générer Prépa ({selectedOrders.length})</span>
                                                                <span className="sm:hidden">Génér. ({selectedOrders.length})</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </button>
                                                <button
                                                    className={`flex items-center justify-center text-xs sm:text-sm ${
                                                        !isDownloadingPrep
                                                            ? 'hover:bg-green-600'
                                                            : 'cursor-wait opacity-70'
                                                    } text-white bg-green-500 py-2 px-3 rounded-xl transition duration-200 h-10 w-full sm:w-auto`}
                                                    onClick={!isDownloadingPrep ? handleExportPreparationSheets : undefined}
                                                    disabled={isDownloadingPrep}
                                                >
                                                    <span className="whitespace-nowrap">
                                                        {isDownloadingPrep ? 'Chargement...' : (
                                                            <>
                                                                <span className="hidden sm:inline">Télécharger Prépa ({selectedOrders.length})</span>
                                                                <span className="sm:hidden">Prépa ({selectedOrders.length})</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </button>
                                                <button
                                                    className={`flex items-center justify-center text-xs sm:text-sm ${
                                                        !isDownloading
                                                            ? 'hover:bg-toola-600'
                                                            : 'cursor-wait opacity-70'
                                                    } text-white bg-toola-500 py-2 px-3 rounded-xl transition duration-200 h-10 w-full sm:w-auto`}
                                                    onClick={!isDownloading ? handleExportDeliveryNotes : undefined}
                                                    disabled={isDownloading}
                                                >
                                                    <span className="whitespace-nowrap">
                                                        {isDownloading ? 'Chargement...' : (
                                                            <>
                                                                <span className="hidden sm:inline">Télécharger BL ({selectedOrders.length})</span>
                                                                <span className="sm:hidden">BL ({selectedOrders.length})</span>
                                                            </>
                                                        )}
                                                    </span>
                                                </button>
                                            </>
                                        )}
                                        {toggleState !== 'devis' && (
                                        <button
                                            className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-xl transition duration-200 h-10 text-xs sm:text-sm font-medium w-full sm:w-auto"
                                            onClick={onQuickOrderOpen}
                                        >
                                            <MdStore className="h-4 w-4" />
                                            <span className="hidden sm:inline">Commande Comptoir</span>
                                            <span className="sm:hidden">Comptoir</span>
                                        </button>
                                        )}
                                        <button
                                            className="flex items-center justify-center bg-toola-500 hover:bg-toola-600 text-white py-2 px-4 rounded-xl transition duration-200 h-10 text-xs sm:text-sm font-medium w-full sm:w-auto"
                                            onClick={() => nav(toggleState === 'devis' ? '/create-devis' : '/create-order')}
                                        >
                                            <span className="hidden sm:inline">{toggleState === 'devis' ? 'Créer un devis' : 'Entrer une commande'}</span>
                                            <span className="sm:hidden">{toggleState === 'devis' ? '+ Devis' : '+ Commande'}</span>
                                        </button>
                                    </div>
                                </div>
                                {toggleState !== 'devis' && (
                                <div className="mt-3 sm:mt-5 flex flex-col sm:flex-row sm:justify-between gap-3 px-2 sm:px-4">
                                    {/* Production status */}
                                    {toggleState === 'pending' && (
                                        <div className="flex flex-wrap gap-2 sm:gap-4">
                                            <p
                                                className={`text-[11px] sm:text-xs ${
                                                    orderType === 'all'
                                                        ? 'text-toola-500 underline font-medium'
                                                        : 'text-toola-500/80'
                                                } hover:underline hover:cursor-pointer whitespace-nowrap`}
                                                onClick={() => setOrderType('all')}
                                            >
                                                Tout afficher
                                            </p>
                                            <p
                                                className={`text-[11px] sm:text-xs ${
                                                    orderType === 'preparing'
                                                        ? 'text-toola-500 underline font-medium'
                                                        : 'text-toola-500/80'
                                                } hover:underline hover:cursor-pointer whitespace-nowrap`}
                                                onClick={() => setOrderType('preparing')}
                                            >
                                                En préparation
                                            </p>
                                            <p
                                                className={`text-[11px] sm:text-xs ${
                                                    orderType === 'ready'
                                                        ? 'text-toola-500 underline font-medium'
                                                        : 'text-toola-500/80'
                                                } hover:underline hover:cursor-pointer whitespace-nowrap`}
                                                onClick={() => setOrderType('ready')}
                                            >
                                                Préparées
                                            </p>
                                            {ordersMeta && (ordersMeta.draft ?? 0) > 0 && (
                                                <p
                                                    className="text-[11px] sm:text-xs text-amber-500/90 hover:underline hover:cursor-pointer whitespace-nowrap"
                                                    onClick={() => changeToogleState('draft')}
                                                >
                                                    {ordersMeta.draft} commande{ordersMeta.draft > 1 ? 's' : ''} en brouillon
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    {/* Sort */}
                                    <div className="flex flex-wrap gap-2">
                                        <p className="text-[11px] sm:text-xs text-gray-600">Trier:</p>
                                        <p
                                            className={`text-[11px] sm:text-xs ${
                                                sortBy === 'deliveryDate'
                                                    ? 'text-toola-500 underline font-medium'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer whitespace-nowrap`}
                                            onClick={() => setSortBy('deliveryDate')}
                                        >
                                            Livraison
                                        </p>
                                        <p
                                            className={`text-[11px] sm:text-xs ${
                                                sortBy === 'createdAt'
                                                    ? 'text-toola-500 underline font-medium'
                                                    : 'text-toola-500/80'
                                            } hover:underline hover:cursor-pointer whitespace-nowrap`}
                                            onClick={() => setSortBy('createdAt')}
                                        >
                                            Création
                                        </p>
                                    </div>
                                </div>
                                )}
                            </>
                        }
                        {toggleState !== 'devis' ? (
                        <>
                        <div className="hidden lg:block absolute left-6 top-6">
                            <div className="flex gap-2">
                                <MdGridView
                                    size={20}
                                    className={`${
                                        view === 'grid' && 'text-toola-500'
                                    } cursor-pointer`}
                                    onClick={() => setView('grid')}
                                />
                                <MdDehaze
                                    size={20}
                                    className={`${
                                        view === 'list' && 'text-toola-500'
                                    } cursor-pointer`}
                                    onClick={() => setView('list')}
                                />
                            </div>
                        </div>
                        <div
                            className={`mt-2 grid grid-cols-1 gap-2 ${
                                view === 'grid' &&
                                'gap-5 md:grid-cols-2 2xl:grid-cols-4'
                            }`}
                        >
                            {/* gap-5 md:grid-cols-2 2xl:grid-cols-4 */}
                            {/* No Order */}
                            {orders && orders.length === 0 && (
                                <div className="col-span-12 mt-10">
                                    <img
                                        className="mx-auto flex h-[250px] w-[250px] items-center justify-center"
                                        src={orderImg}
                                        alt=""
                                    />
                                    <h4 className="col-span-12 my-auto mt-3 text-center text-2xl font-medium text-navy-700 dark:text-white">
                                        Gérer vos commandes facilement
                                    </h4>
                                    <p className="col-span-12 my-auto mt-3 text-center text-base text-navy-700 dark:text-white">
                                        Vous pouvez aussi créer votre page de
                                        commande pour automatiser leur
                                        réception.
                                    </p>
                                    <div className="font-large mt-5 flex items-center justify-center gap-5 rounded-xl">
                                        <button
                                            className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                            onClick={() => nav('/create-order')}
                                        >
                                            Créer une page de commande
                                        </button>
                                        <button
                                            className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                            onClick={() => nav('/create-order')}
                                        >
                                            Ajouter une commande
                                        </button>
                                    </div>
                                </div>
                            )}

                            {orders && view === 'list' && (
                                <div className="rounded-lg border border-r">
                                    <div className="relative overflow-x-auto">
                                        <table className="w-full text-sm text-left rtl:text-right text-gray-500">
                                            <thead className="text-gray-700 bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-2 py-3">
                                                        <input
                                                            type="checkbox" 
                                                            onChange={toggleAllOrders} 
                                                            checked={orders && orders.filter((c: any) => c.status === toggleState).length > 0 && selectedOrders.length === orders.filter((c: any) => c.status === toggleState).length}
                                                            className="h-4 w-4 rounded border-gray-300 text-toola-600 focus:ring-toola-500"
                                                        />
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        # Commande
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Client
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Statut
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Date de livraison
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Articles
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Marge Brut
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Prix Total HT
                                                    </th>
                                                    <th
                                                        scope="col"
                                                        className="px-2 py-3"
                                                    >
                                                        Prix Total TTC
                                                    </th>                                        
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getFilteredOrders(orders).map(
                                                        (
                                                            item: any,
                                                            index: number,
                                                        ) => (
                                                            (() => {
                                                                const marginSummary =
                                                                    getOrderMarginSummary(item);
                                                                const displayedTotalTTC =
                                                                    getDisplayedTotalTTC(item);
                                                                return (
                                                            <tr
                                                                className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() =>
                                                                    nav(
                                                                        `/order/${item._id}`,
                                                                    )
                                                                }
                                                            >
                                                                <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={selectedOrders.includes(item._id)} 
                                                                        onChange={() => toggleOrderSelection(item._id)} 
                                                                        className="h-4 w-4 rounded border-gray-300 text-toola-600 focus:ring-toola-500"
                                                                    />
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {/* Created 10 minutes ago ? Live ping */}

                                                                    <p className="flex my-auto">
                                                                        #
                                                                        {
                                                                            item.orderId
                                                                        }
                                                                    </p>
                                                                    <div className="flex my-auto gap-2">
                                                                        {isWithin30Minutes(
                                                                            item?.createdAt,
                                                                        ) && (
                                                                            <span className="flex my-auto relative h-2 w-2">
                                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-toola-500/50 opacity-75"></span>
                                                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-toola-500/50"></span>
                                                                            </span>
                                                                        )}
                                                                        <p className="text-xs text-gray-700">
                                                                            {timeAgo(
                                                                                item.createdAt,
                                                                            )}
                                                                        </p>
                                                                    </div>

                                                                        <p className='text-xs text-gray-700'>
                                                                            {item.createdBy && item.origin !== 'command-page'
                                                                                ? `Créé par ${item.createdBy.fullName || item.createdBy.email?.split('@')[1] }`
                                                                                : '' }
                                                                            {item.origin === 'command-page' && item.customer
                                                                                && (item.contactEmail
                                                                                    ? `Commandé par ${item.contactEmail}`
                                                                                    : 'Créé via le shop')
                                                                                }
                                                                        </p>
                                                                </td>
                                                                <td className="px-2 max-w-[180px] py-3">
                                                                    {item.displayName ||
                                                                        item.company}
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {item.invoiced ? (
                                                                        <span className="text-xs bg-green-200 text-green-700 px-2 py-1 rounded-full">
                                                                            Facturée
                                                                        </span>
                                                                    ) : item.status ===
                                                                      'already-invoiced' ? (
                                                                        <span className="text-xs bg-purple-200 text-purple-700 px-2 py-1 rounded-full">
                                                                            Déjà
                                                                            facturé
                                                                        </span>
                                                                    ) : item.status ===
                                                                      'pending' ? (
                                                                        <span className="text-xs bg-yellow-200 text-yellow-700 px-2 py-1 rounded-full">
                                                                            En
                                                                            cours
                                                                        </span>
                                                                    ) : item.status ===
                                                                      'draft' ? (
                                                                        <span className="text-xs bg-orange-200 text-orange-600 px-2 py-1 rounded-full font-medium">
                                                                            Brouillon
                                                                        </span>
                                                                    ) : item.status ===
                                                                      'delivered' ? (
                                                                        <span className="text-xs bg-toola-500/30 text-toola-500 px-2 py-1 rounded-full">
                                                                            Livrée
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-xs bg-red-200 text-red-700 px-2 py-1 rounded-full">
                                                                            Annulée
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {dateToFrenchShort(
                                                                        item.deliveryDate,
                                                                    )}
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {
                                                                        item
                                                                            ?.items
                                                                            .length
                                                                    }
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {marginSummary.hasComputedMargin ? (
                                                                        <>
                                                                            <p>
                                                                                {marginSummary.totalMargin.toFixed(2)}
                                                                                {' €'}
                                                                            </p>
                                                                            {marginSummary.marginRate !== null && (
                                                                                <p className="text-xs text-gray-700">
                                                                                    {parseFloat(
                                                                                        marginSummary.marginRate.toFixed(
                                                                                            2,
                                                                                        ),
                                                                                    )}
                                                                                    %
                                                                                </p>
                                                                            )}
                                                                            {marginSummary.excludedCount > 0 && (
                                                                                <p className="text-xs text-orange-500">
                                                                                    {marginSummary.excludedCount}{' '}
                                                                                    produit
                                                                                    {marginSummary.excludedCount > 1
                                                                                        ? 's'
                                                                                        : ''}{' '}
                                                                                    sans coût exclu
                                                                                    {marginSummary.excludedCount > 1
                                                                                        ? 's'
                                                                                        : ''}
                                                                                </p>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        'N/A'
                                                                    )}
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {
                                                                        item?.totalHT
                                                                    }{' '}
                                                                    €
                                                                </td>
                                                                <td className="px-2 py-3">
                                                                    {displayedTotalTTC.toFixed(2)} €
                                                                </td>
                                                            </tr>
                                                                );
                                                            })()
                                                        ),
                                                    )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {/* Cards list */}
                            {orders &&
                                view === 'grid' &&
                                orders
                                    .filter((c: any) =>
                                        toggleState === 'pending'
                                            ? c.status === 'pending' || c.status === 'draft'
                                            : c.status === toggleState,
                                    )
                                    .map((order: any) => {
                                        return (
                                            <OrderCard
                                                id={order._id}
                                                company={order.company}
                                                displayName={order.displayName}
                                                orderId={order.orderId}
                                                deliveryDate={
                                                    order.deliveryDate
                                                }
                                                createdAt={order.createdAt}
                                                items={order.items}
                                                viewed={order.viewed}
                                                orderType={orderType}
                                                status={order.status}
                                            />
                                        );
                                    })}

                            {/* Skelleton loader */}
                            {!orders &&
                                Array(limitPerPage)
                                    .fill(<LoadingCard />)
                                    .map((order: any) => {
                                        return order;
                                    })}
                        </div>
                        {/* pagination */}
                        {orders && (
                            <Pagination
                                extra="mt-3"
                                text={true}
                                maxItems={
                                    ordersMeta ? ordersMeta.sectionTotal : 12
                                }
                                itemsPerPage={[8, 12, 16, 30, 50, 100]}
                                defaultItemsPerPage={limitPerPage}
                                page={page}
                                onChange={(page: number, limit: number) =>
                                    fetchOrders(
                                        page,
                                        limit,
                                        toggleState,
                                        sortBy,
                                        selectedTour,
                                    )
                                }
                            />
                        )}
                        </>
                        ) : (
                            <DevisTable search={search} range={range} />
                        )}
                    </Card>
                </div>
            </div>
            
            <QuickOrder 
                isOpen={isQuickOrderOpen} 
                onClose={onQuickOrderClose} 
                onOrderCreated={() => {
                    fetchOrders(page, limitPerPage, toggleState, sortBy, selectedTour);
                    initOrdersDashboard();
                }}
            />
        </>
    );
};

export default ListOrders;
